import { QUIZ_ATTEMPT_STATUS } from 'constants/driving-elearning.constant';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
// Phase 1B + 2A: quiz/course/quizzes-by-courses functions go through the
// BE proxy via elearningApi. The legacy moodleApi import is no longer
// needed on this page.
import {
  Card,
  Row,
  Col,
  Container,
  Accordion,
  Modal,
  Badge,
} from 'react-bootstrap';
import {
  PrimaryButton,
  Spinner,
  colors,
  radius,
  shadow,
  spacing,
  typography,
} from 'design-system';
import QuestionItem from '../components/QuestionItem';
import ClozeQuestionItem from '../components/ClozeQuestionItem';
import QuestionNavigator from '../components/QuestionNavigator';
import { toastWrapper } from 'utils';
import { usePromptWithUnload } from 'hooks/usePromptWithUnload';
import Timer from '../components/Timer';
import QuestionReviewModal from '../components/QuestionReviewModal';
import { PATH } from 'constants/path';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectElearningCourses,
  selectElearningData,
  selectElearningSettings,
  selectQuizzes,
  updateElearningData,
} from 'store/elearning.slice';
import {
  MdChecklist,
  MdOutlineQuiz,
  MdOutlineTimelapse,
  MdSubject,
  MdWarning,
} from 'react-icons/md';
import BackButton from 'components/BackButton';
import { parseQuestionHTML } from 'utils/elearning.utils';
import useSingleTab from 'hooks/useSingleTab';
import { formatTime } from 'utils/commonUtils';
import { buildAttemptDataMap, attemptAccumulatedTime } from 'utils/quiz-attempt-time.util';
import elearningApi from 'api/elearningApi';
import { shouldBlockFromServer, vnDateKey } from 'utils/daily-limit-event.util';

const MOBILE = '@media (max-width: 768px)';

// Định dạng số giây chờ (retryAfter từ rate-limiter) thành chuỗi thân thiện:
// "45 giây", "5 phút", "4 phút 30 giây".
function formatWait(sec) {
  const s = Math.max(0, Math.ceil(Number(sec) || 0));
  if (s < 60) return `${s} giây`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m} phút ${r} giây` : `${m} phút`;
}

function ElearningStudentTestDetailPage() {
  useSingleTab('/elearning/student/test', 'elearning_module');
  const testId = useParams().id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('c') ? parseInt(searchParams.get('c')) : null;
  const [userAttempts, setUserAttempts] = useState([]);
  // Số câu đã trả lời của từng lượt (backend đếm) → giờ tích luỹ từng lượt.
  const [attemptDataMap, setAttemptDataMap] = useState(null);
  const [quizAttempt, setQuizAttempt] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [quizAttemptData, setQuizAttemptData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [slotAnswers, setSlotAnswers] = useState(null);
  const [slot, setSlot] = useState(null);
  // Collapsed by default — HV mở lại trang quiz không cần thấy ngay
  // lịch sử cũ. Auto-expand sau submit (xem handleFinishQuiz) để
  // highlight attempt vừa nộp.
  const [showHistory, setShowHistory] = useState(false);
  // Limit 5 attempt gần nhất; nếu HV làm nhiều hơn thì hiện "Xem thêm".
  const [showAllAttempts, setShowAllAttempts] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAttemptSummary, setLoadingAttemptSummary] = useState(true);
  const [attemptSummary, setAttemptSummary] = useState(null);
  const [showQuestionReviewModal, setShowQuestionReviewModal] = useState(false);
  // Tách 2 trạng thái — trước đây dùng chung `preventFinish` cho cả
  // "đang lưu câu trả lời" và "đang nộp bài" → race: user click "Kết
  // thúc bài làm" khi debounced save chưa xong → handleFinishQuiz
  // silent-return (preventFinish=true). Chia ra cho thấy rõ ý định:
  //   savingAnswers — disable button "Tiếp tục/Lưu" khi save in-flight
  //   isFinishing   — re-entrancy guard cho chính handleFinishQuiz
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [startingAttempt, setStartingAttempt] = useState(false);
  const [preparingSubmit, setPreparingSubmit] = useState(false);
  const [preparingLabel, setPreparingLabel] = useState('Vui lòng chờ...');
  const dispatch = useDispatch();
  const elearningData = useSelector(selectElearningData);
  const { timeLimitPerDay, totalTodayTime } = elearningData;
  const elearningSettings = useSelector(selectElearningSettings);
  const elearningUserId = elearningData?.elearningUser?.elearningUserId;
  const elearningCourses = useSelector(selectElearningCourses);
  const quizzesMap = useSelector(selectQuizzes);
  // Fallback course doc cho trường hợp Redux không có (HV browse quiz của
  // course không thuộc classification='inprogress' — vd course đã finished
  // hoặc upcoming). Fetch lazy chỉ khi Redux confirmed miss → giữ tối ưu
  // B1 cho path common (course inprogress, không cần round-trip).
  const [courseFallback, setCourseFallback] = useState(null);
  const courseFromRedux = useMemo(
    () => (courseId && elearningCourses ? elearningCourses[courseId] : null),
    [elearningCourses, courseId]
  );
  const course = courseFromRedux || courseFallback;
  const cachedQuizzes = courseId ? quizzesMap?.[courseId] : null;
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showOverLimitModal, setShowOverLimitModal] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  const [maxAllowedForSubmit, setMaxAllowedForSubmit] = useState(null);
  const [trimmingAnswers, setTrimmingAnswers] = useState(false);
  const limitModalDismissedRef = useRef(false);

  const timePerQuestion = elearningSettings?.[courseId]?.timePerQuestionInMinute ?? 0.5;

  // Tải lại số câu từng lượt mỗi khi danh sách lượt đổi (mở trang, nộp bài).
  const attemptsKey = userAttempts.map((a) => `${a.id}:${a.state}`).join(',');
  useEffect(() => {
    if (!elearningUserId || !testId || !attemptsKey) return undefined;
    let cancelled = false;
    elearningApi.getBatchQuizAttempts(elearningUserId, [testId])
      .then((res) => { if (!cancelled) setAttemptDataMap(buildAttemptDataMap(res?.data, testId)); })
      .catch(() => { if (!cancelled) setAttemptDataMap(null); });
    return () => { cancelled = true; };
  }, [elearningUserId, testId, attemptsKey]);
  // serverTime chỉ được set khi HV bấm "Nộp bài" (submit-time check).
  // Trước khi đó, fallback sang Redux value (populated bởi global poll
  // ở layout) cho computed display values + maxQuestions modal logic.
  const serverTotalTime = serverTime?.totalTodayTime;
  const serverLimitPerDay = serverTime?.timeLimitPerDay;
  const displayTotalTime = serverTotalTime ?? totalTodayTime ?? 0;
  const maxDailySeconds = serverLimitPerDay ?? timeLimitPerDay ?? 8 * 60 * 60;
  const baseRemainingSeconds = Math.max(0, maxDailySeconds - displayTotalTime);
  const maxQuestions = Math.floor(baseRemainingSeconds / (timePerQuestion * 60));

  const answeredCount = useMemo(() => {
    if (!attemptSummary?.questions) return 0;
    return attemptSummary.questions.filter((q) => q.stateclass !== 'notyetanswered').length;
  }, [attemptSummary?.questions]);

  const isAtQuestionLimit = maxQuestions > 0 && answeredCount >= maxQuestions;
  const isOverQuestionLimit = maxQuestions > 0 && answeredCount > maxQuestions;
  const answeredTimeSeconds = answeredCount * timePerQuestion * 60;
  const remainingSeconds = Math.max(0, baseRemainingSeconds - answeredTimeSeconds);

  useEffect(() => {
    if (quizAttempt?.id && maxQuestions > 0 && answeredCount >= maxQuestions && !limitModalDismissedRef.current) {
      setShowLimitModal(true);
    }
  }, [answeredCount, maxQuestions, quizAttempt?.id]);

  // Tải thời gian học hôm nay ASYNC khi vào attempt → khung "thời gian còn
  // lại" hiện số liệu mới mà KHÔNG block luồng làm bài (fire-and-forget, set
  // state khi xong). Trước đây fetch này await ở start nên bị bỏ để khỏi
  // chặn; giờ chạy nền nên an toàn. Redux poll ở layout vẫn là nguồn fallback.
  useEffect(() => {
    if (!quizAttempt?.id) return;
    let cancelled = false;
    elearningApi.getMyTodayTime()
      .then((res) => {
        if (cancelled || !res?.data) return;
        setServerTime(res.data);
        const { totalTodayTime: t, timeLimitPerDay: l, isLimitExceeded: e, timePerCourse: c } = res.data;
        dispatch(updateElearningData({
          totalTodayTime: t, timeLimitPerDay: l, isLimitExceeded: e, timePerCourse: c,
          todayTimeUpdatedAt: Date.now(),
        }));
      })
      .catch(() => { /* non-blocking — Redux poll vẫn cập nhật */ });
    return () => { cancelled = true; };
  }, [quizAttempt?.id, dispatch]);


  usePromptWithUnload(
    'Bạn có chắc muốn rời đi? Dữ liệu chưa được lưu.',
    quizAttempt?.id != null
  );

  useEffect(() => {
    if (!testId || !courseId) return;

    // Reset state khi chuyển bài
    setUserAttempts([]);
    setQuizAttempt(null);
    setCurrentPage(0);
    setQuizAttemptData(null);
    setAnswers({});
    setSlotAnswers(null);
    setSlot(null);
    setShowHistory(false);
    setShowAllAttempts(false);
    setQuiz(null);
    setCourseFallback(null);
    setAttemptSummary(null);
    setShowQuestionReviewModal(false);
    setShowLimitModal(false);
    setShowOverLimitModal(false);
    setServerTime(null);
    setMaxAllowedForSubmit(null);
    setTrimmingAnswers(false);
    limitModalDismissedRef.current = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Skip the per-course quizzes fetch when Redux already has it
        // (visited Test list or another quiz in same course this session).
        const quizzesPromise = cachedQuizzes
          ? Promise.resolve(cachedQuizzes)
          : elearningApi.quizzesByCourses([courseId]);

        const [attemptsRes, quizzesRes] = await Promise.all([
          elearningApi.quizGetUserAttempts(testId, 'all'),
          quizzesPromise,
        ]);

        if (attemptsRes?.attempts.at(-1)?.state === 'inprogress') {
          // KHÔNG fetch getMyTodayTime ở entry — gate daily-time chỉ
          // chạy khi HV bấm "Nộp bài" (xem onClick outer submit button).
          const inprogressAttempt = attemptsRes.attempts.at(-1);
          setQuizAttempt(inprogressAttempt);
          // Anti-cheat tracking — HV reload trang giữa chừng. Idempotent
          // upsert ở BE, không tạo token mới nếu đã có. Best-effort.
          try {
            elearningApi.startQuizAttemptTracking(Number(testId), inprogressAttempt.id).catch(() => {});
          } catch (_) { /* noop */ }
        }

        setUserAttempts(attemptsRes.attempts);
        const foundQuiz = quizzesRes?.find((q) => q.id == testId);
        if (foundQuiz) {
          setQuiz(foundQuiz);
        }
        if (!cachedQuizzes && quizzesRes) {
          dispatch(
            updateElearningData({
              quizzes: { ...(quizzesMap || {}), [courseId]: quizzesRes },
            })
          );
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) fetchData();
    // quizzesMap is intentionally omitted — we only need to seed it once
    // per (testId, courseId) mount; later updates from other pages shouldn't
    // re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId, courseId]);

  useEffect(() => {
    if (quizAttempt?.id) {
      getQuizAttemptSummary(quizAttempt?.id);
    }
  }, [quizAttempt?.id]);

  // Lazy fallback fetch: chỉ chạy khi Redux đã hydrate (elearningCourses
  // không còn null) MÀ vẫn không có courseId này (course finished /
  // upcoming không thuộc classification 'inprogress'). Common path —
  // course inprogress — sẽ skip fetch nhờ `courseFromRedux` đã có giá trị.
  useEffect(() => {
    if (!courseId) return;
    if (courseFromRedux) return;       // Redux đã có → khỏi fetch
    if (elearningCourses == null) return; // Redux đang load → chờ
    if (courseFallback?.id === courseId) return; // đã fetch rồi
    elearningApi
      .courseById(courseId)
      .then((data) => setCourseFallback(data || null))
      .catch((err) => {
        console.error('Error fetching course fallback:', err);
      });
  }, [courseId, courseFromRedux, elearningCourses, courseFallback?.id]);

  const getQuizAttemptSummary = async (attemptId) => {
    try {
      setLoadingAttemptSummary(true);
      const data = await elearningApi.quizGetAttemptSummary(attemptId);
      setAttemptSummary(data);
    } catch (error) {
      console.error('Error fetching attempt summary:', error);
    } finally {
      setLoadingAttemptSummary(false);
    }
  };

  const startNewAttempt = async () => {
    if (!testId) return;

    // KHÔNG fetch getMyTodayTime ở đây — gate daily-time chuyển sang
    // lúc HV bấm "Nộp bài" (xem onClick của outer submit button). Lý do:
    //   - HV cần làm bài thử trước khi biết có vượt quota — UX tự nhiên
    //     hơn so với chặn ngay từ nút Start.
    //   - Logic over-limit ở submit (showOverLimitModal) đã handle trim
    //     câu thừa đến đúng quota, nên không mất dữ liệu.
    setStartingAttempt(true);
    try {
      const attemptData = await elearningApi.quizStartAttempt(testId);
      setQuizAttempt(attemptData.attempt);
      // Anti-cheat tracking — best-effort. Lỗi mạng/BE down → swallow,
      // HV vẫn làm bài bình thường. Cron orphan-check sẽ catch sau.
      try {
        elearningApi.startQuizAttemptTracking(Number(testId), attemptData.attempt?.id).catch(() => {});
      } catch (_) { /* noop */ }
    } catch (error) {
      console.error('Error starting attempt:', error);
      // Hiện lỗi rõ ràng thay vì im lặng (trước đây chỉ console.error → HV
      // bấm "Bắt đầu" mà không thấy phản hồi gì). Ưu tiên message từ BE (vd
      // START_COOLDOWN khi bắt đầu quá nhanh), kèm số giây chờ nếu có.
      const data = error?.response?.data;
      const baseMsg = data?.message || 'Không bắt đầu được bài làm. Vui lòng thử lại.';
      const msg = data?.retryAfter ? `${baseMsg} (vui lòng đợi ${formatWait(data.retryAfter)})` : baseMsg;
      toastWrapper(msg, 'error');
    } finally {
      setStartingAttempt(false);
    }
  };

  // Per-page cache: Map<page, attemptData>. Tránh re-fetch khi HV click
  // QuestionNavigator qua lại các câu — BE rate-limit 1 req/1s ở
  // /quiz/attempt/:id/data sẽ 429 với click nhanh, ngay cả khi data tĩnh
  // giữa các save. Sequencecheck thay đổi sau mỗi save → invalidate page
  // hiện tại trong saveAnswer success path để fetch fresh lần kế.
  //
  // Ref vì cập nhật KHÔNG cần trigger re-render (re-render bởi
  // setQuizAttemptData ở callsite). Map mới khi attemptId đổi (xem useEffect).
  const attemptDataCacheRef = useRef(new Map());
  // Mirror currentPage sang ref để saveAnswer (deps quizAttempt?.id) đọc
  // được page hiện tại lúc invalidate cache mà không phải lệ thuộc state
  // closure → tránh stale.
  const currentPageRef = useRef(0);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Reset cache khi đổi attempt (HV start new / continue khác)
  useEffect(() => {
    attemptDataCacheRef.current = new Map();
  }, [quizAttempt?.id]);

  const handleGetAttemptData = (attemptId, page) => {
    const cached = attemptDataCacheRef.current.get(page);
    if (cached) {
      setQuizAttemptData(cached);
      setShowHistory(false);
      return;
    }
    elearningApi
      .quizGetAttemptData(attemptId, page)
      .then((data) => {
        attemptDataCacheRef.current.set(page, data);
        setQuizAttemptData(data);
      })
      .catch((error) => {
        setQuizAttemptData(null);
        setQuizAttempt(null);
        console.error('Error fetching attempt data:', error);
        window.location.reload();
      })
      .finally(() => {
        setShowHistory(false);
      });
  };

  useEffect(() => {
    if (quizAttempt?.id != null) {
      handleGetAttemptData(quizAttempt?.id, currentPage);
    }
  }, [quizAttempt?.id, currentPage]);

  // Prefetch next page ngay sau khi current page data load xong.
  // BE rate-limit relax tới 30 req/10s per (HV, attempt) → fire ngay,
  // không cần delay. Cache hit khi user click Tiếp → 0 perceived latency.
  //
  // Trigger phụ thuộc `quizAttemptData` (current page payload) để bảo
  // đảm prefetch chạy SAU khi current load xong, không cùng lúc.
  //
  // Skip nếu: chưa biết total, đã ở câu cuối, hoặc page kế đã cached.
  // Silent on error — handleGetAttemptData sẽ retry khi user thực sự
  // nav tới page đó.
  useEffect(() => {
    if (!quizAttempt?.id) return;
    if (!quizAttemptData) return; // wait current page settled
    const total = attemptSummary?.questions?.length;
    if (!total) return;
    const nextPage = currentPage + 1;
    if (nextPage >= total) return;
    if (attemptDataCacheRef.current.has(nextPage)) return;

    let cancelled = false;
    elearningApi
      .quizGetAttemptData(quizAttempt.id, nextPage)
      .then((data) => {
        if (!cancelled) attemptDataCacheRef.current.set(nextPage, data);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [quizAttempt?.id, currentPage, quizAttemptData, attemptSummary?.questions?.length]);
  // Plain async — không debounce (trước đây debounce(fn, 0) tương đương
  // call ngay nhưng mất khả năng await result). Caller cần await trước
  // khi setCurrentPage để button hiển thị loading đầy đủ rồi mới chuyển
  // câu kế. Throw on error để caller biết save fail → KHÔNG advance page.
  const saveAnswer = useCallback(async (slotAnswers, slot) => {
    const buildData = (sa) => Object.entries(sa).map(([key, value]) => ({ name: key, value }));

    // Lỗi nhất thời đáng retry: mất mạng/timeout (không response), 429
    // (save rate-limit 1/2s), 5xx (Moodle WS chập / 502), hoặc 409
    // (sequencecheck lệch). Đều cứu được bằng refetch sequencecheck mới rồi
    // gửi lại — mod_quiz_process_attempt ghi đè cùng slot nên idempotent.
    const isTransient = (err) => {
      if (!err?.response) return true;
      const s = err.response.status;
      return s === 429 || s === 409 || s >= 500;
    };
    // Lấy sequencecheck mới cho slot rồi vá payload (sequencecheck có thể đã
    // nhích nếu lần save trước lọt một phần / có save song song).
    const withFreshSequenceCheck = async (sa) => {
      try {
        const fresh = await elearningApi.quizGetAttemptData(quizAttempt?.id, currentPageRef.current);
        attemptDataCacheRef.current.set(currentPageRef.current, fresh);
        const q = (fresh?.questions || []).find((x) => x.slot === slot);
        if (q?.sequencecheck == null) return sa;
        const patched = { ...sa };
        const seqKey = Object.keys(patched).find((k) => k.includes(`:${slot}_:sequencecheck`));
        if (seqKey) patched[seqKey] = q.sequencecheck;
        return patched;
      } catch (_) {
        return sa;
      }
    };
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    try {
      setSavingAnswers(true);
      try {
        await elearningApi.quizSaveAnswers(quizAttempt?.id, buildData(slotAnswers));
      } catch (err) {
        if (!isTransient(err)) throw err;
        // 429 → chờ qua cửa sổ rate-limit save (1 req/2s) trước khi gửi lại.
        if (err?.response?.status === 429) await sleep(2200);
        const patched = await withFreshSequenceCheck(slotAnswers);
        await elearningApi.quizSaveAnswers(quizAttempt?.id, buildData(patched));
      }
      // Invalidate cache page vừa save — Moodle increment sequencecheck
      // cho slot này, fetch lần kế phải lấy fresh để save kế tiếp không
      // bị reject (anti-replay). Các page khác không ảnh hưởng nên giữ.
      attemptDataCacheRef.current.delete(currentPageRef.current);
      setAttemptSummary((prev) => {
        const updated = { ...prev };
        updated.questions = updated.questions.map((question) => {
          if (question.slot === slot) {
            return {
              ...question,
              stateclass: 'answersaved',
            };
          }
          return question;
        });
        return updated;
      });
    } catch (err) {
      console.error('Error saving answer:', err);
      toastWrapper('Có lỗi xảy ra khi lưu câu trả lời!', 'error');
      throw err;
    } finally {
      setSavingAnswers(false);
    }
  }, [quizAttempt?.id]);

  const handleAnswerChange = (slot, payload) => {
    setSlot(slot);
    setAnswers((prev) => {
      const updated = { ...prev };
      payload.forEach(({ name, value }) => {
        updated[name] = value;
      });
      const slotAnswers = Object.fromEntries(
        Object.entries(updated).filter(([key]) => key.includes(`:${slot}_`))
      );
      setSlotAnswers(slotAnswers);
      return updated;
    });
  };

  const continueQuizAttempt = (attempt) => {
    // Không fetch getMyTodayTime — daily-time chỉ check ở submit.
    setQuizAttempt(attempt);
    setShowHistory(false);
  };

  const clearExcessAnswers = async (maxToKeep) => {
    if (!quizAttempt?.id || !attemptSummary?.questions) return;

    const answeredQuestions = attemptSummary.questions
      .filter((q) => q.stateclass !== 'notyetanswered')
      .sort((a, b) => a.slot - b.slot);

    if (answeredQuestions.length <= maxToKeep) return;

    const excessQuestions = answeredQuestions.slice(maxToKeep);
    const clearPayloads = [];

    for (const q of excessQuestions) {
      const parsed = parseQuestionHTML(q.html);
      if (!parsed?.id) continue;
      if (parsed.type === 'multianswer') {
        // Cloze: mỗi sub-question có input name riêng (…_subN_answer)
        for (const sub of parsed.subQuestions || []) {
          if (sub.inputName) clearPayloads.push({ name: sub.inputName, value: '-1' });
        }
        clearPayloads.push(
          { name: `q${parsed.id}:${q.slot}_:sequencecheck`, value: q.sequencecheck?.toString() || '0' },
          { name: `q${parsed.id}:${q.slot}_:flagged`, value: '0' }
        );
        continue;
      }
      clearPayloads.push(
        { name: `q${parsed.id}:${q.slot}_answer`, value: '-1' },
        { name: `q${parsed.id}:${q.slot}_:sequencecheck`, value: q.sequencecheck?.toString() || '0' },
        { name: `q${parsed.id}:${q.slot}_:flagged`, value: '0' }
      );
    }

    if (clearPayloads.length === 0) return;

    await elearningApi.quizSaveAnswers(quizAttempt.id, clearPayloads);
  };

  const handleFinishQuiz = async (timeUp = false) => {
    // Chỉ guard re-entrancy của chính finish — KHÔNG block khi đang
    // save answer. Save sẽ tiếp tục chạy song song; quiz_save_responses
    // ở Moodle là idempotent (overwrite cùng slot) + BE proxy đã serialize
    // qua attempt mutex nên không có race nguy hiểm.
    if (!quizAttempt?.id || isFinishing) return;
    setIsFinishing(true);

    try {
      const finishedAttemptId = quizAttempt?.id;
      await elearningApi.quizFinishAttempt(finishedAttemptId, timeUp, courseId);
      toastWrapper('Nộp bài thành công!', 'success');

      // Anti-cheat tracking — best-effort. Báo BE attempt đã consumed.
      // Lỗi → swallow, không ảnh hưởng HV.
      try {
        elearningApi.finishQuizAttemptTracking(finishedAttemptId).catch(() => {});
      } catch (_) { /* noop */ }

      // Cleanup UI ngay → HV thấy nút "Thực hiện lại" lập tức, không bị
      // khựng chờ getMyTodayTime hoàn tất. Trước đây await này block
      // ~300-800ms sau khi toast hiện, gây cảm giác đơ.
      setQuizAttempt(null);
      setQuizAttemptData(null);
      setAttemptSummary(null);
      setAnswers({});
      setCurrentPage(0);
      setShowHistory(true);
      elearningApi.quizGetUserAttempts(testId, 'all')
        .then((data) => {
          setUserAttempts(data.attempts);
        })
        .catch((error) => {
          console.error('Error fetching user attempts:', error);
        });

      // Fire-and-forget: cập nhật Redux + localStorage cho cap-block
      // logic dùng ở book/video. HV có thể đã rời page trước khi
      // request này resolve — vẫn OK vì Redux dispatch global, lần
      // mount kế của book/video vẫn query lại fresh.
      elearningApi.getMyTodayTime().then((freshTimeRes) => {
        if (!freshTimeRes?.data) return;
        const { totalTodayTime: latestTotal, timeLimitPerDay: latestLimit, timePerCourse } = freshTimeRes.data;
        setServerTime(freshTimeRes.data);
        const shouldBlock = shouldBlockFromServer(freshTimeRes.data);
        if (shouldBlock) {
          localStorage.setItem('elearning_limit_blocked', JSON.stringify({
            blocked: true,
            date: vnDateKey(),
          }));
        }
        dispatch(updateElearningData({
          totalTodayTime: latestTotal,
          timeLimitPerDay: latestLimit,
          isLimitExceeded: shouldBlock,
          timePerCourse,
          todayTimeUpdatedAt: Date.now(),
        }));
      }).catch(() => { /* swallow — ELearning layout poll sẽ pick up */ });
    } catch (error) {
      console.error('Error finishing quiz:', error);
      // Rate-limit (nộp bài quá sớm / quá nhiều) → hiện thời gian chờ rõ ràng
      // từ retryAfter thay vì thông báo lỗi chung chung.
      const data = error?.response?.data;
      if (error?.response?.status === 429) {
        const base = data?.message || 'Bạn nộp bài quá nhanh.';
        toastWrapper(
          data?.retryAfter ? `${base} (vui lòng đợi ${formatWait(data.retryAfter)})` : base,
          'error',
        );
      } else if (error?.response?.status === 400 && data?.code === 'EXCEEDS_DAILY_LIMIT') {
        // BE chặn finish vì vượt cap. Mở lại trim modal với data fresh từ
        // BE (server-side compute → trustworthy). HV phải trim đúng N câu
        // mới nộp được. Detail nằm trong data.data (flat error shape ở
        // _quizProxyHandler catch block).
        const detail = data?.data || {};
        setServerTime({
          totalTodayTime: detail.totalTodayTime,
          timeLimitPerDay: detail.timeLimitPerDay,
        });
        setMaxAllowedForSubmit(detail.maxQuestionsAllowed ?? 0);
        setShowQuestionReviewModal(false);
        setShowOverLimitModal(true);
        toastWrapper(data?.message || 'Bạn đã vượt giới hạn thời gian học hôm nay. Vui lòng cắt bớt câu thừa.', 'warning');
      } else {
        toastWrapper('Có lỗi xảy ra khi nộp bài!', 'error');
      }
    } finally {
      setShowQuestionReviewModal(false);
      setIsFinishing(false);
    }
  };

  return (
    <PageWrap>
      <Container fluid className='py-3 py-md-4'>
        {loading ? (
          <Spinner block label='Đang tải dữ liệu...' />
        ) : (
          <>
            <BackButton confirmMessage={quizAttempt?.id ? 'Bạn đang làm bài. Bạn có chắc muốn rời đi? Bài làm chưa nộp sẽ không được tính giờ.' : undefined} />
            {quiz && (
              <>
                <QuizHeader>
                  <QuizTitleRow>
                    <QuizBadge>Bài kiểm tra</QuizBadge>
                    <PageTitle>{quiz.name}</PageTitle>
                  </QuizTitleRow>
                  <StatGrid>
                    <StatCard $tone='subject' $wide>
                      <StatIcon $tone='subject'>
                        <MdSubject size={20} />
                      </StatIcon>
                      <StatBody>
                        <StatLabel>Môn học</StatLabel>
                        <StatValue>
                          {(() => {
                            // Moodle 2 WS trả shape khác nhau:
                            //   - core_course_get_enrolled_courses_by_timeline_classification
                            //     (Layout dùng) → có fullnamedisplay, KHÔNG có displayname
                            //   - core_course_get_courses (courseById fallback)
                            //     → có displayname VÀ fullname
                            // Thử theo thứ tự: displayname > fullnamedisplay > fullname.
                            const name = course?.displayname || course?.fullnamedisplay || course?.fullname;
                            return name || <StatPlaceholder>Đang tải…</StatPlaceholder>;
                          })()}
                        </StatValue>
                      </StatBody>
                    </StatCard>

                    {/* Chỉ hiện ô "Thời gian làm bài" khi quiz CÓ giới hạn —
                        bỏ hẳn khi không giới hạn (thay vì hiện "Không giới hạn"). */}
                    {quiz.timelimit && quiz.timelimit !== 0 ? (
                      <StatCard $tone='time'>
                        <StatIcon $tone='time'>
                          <MdOutlineTimelapse size={20} />
                        </StatIcon>
                        <StatBody>
                          <StatLabel>Thời gian làm bài</StatLabel>
                          <StatValue>{`${Math.floor(quiz.timelimit / 60)} phút`}</StatValue>
                        </StatBody>
                      </StatCard>
                    ) : null}

                    <StatCard $tone='count'>
                      <StatIcon $tone='count'>
                        <MdOutlineQuiz size={20} />
                      </StatIcon>
                      <StatBody>
                        <StatLabel>Số câu hỏi</StatLabel>
                        <StatValue>
                          {quiz?.questioncount || '—'}
                          {quiz?.questioncount ? <StatUnit> câu</StatUnit> : null}
                        </StatValue>
                      </StatBody>
                    </StatCard>

                    <StatCard $tone='pass'>
                      <StatIcon $tone='pass'>
                        <MdChecklist size={20} />
                      </StatIcon>
                      <StatBody>
                        <StatLabel>Điểm cần đạt</StatLabel>
                        <StatValue>
                          {Number(quiz.gradepass) > 0 ? (
                            <>
                              {quiz.gradepass}<StatUnit> / {quiz.sumgrades}</StatUnit>
                            </>
                          ) : (
                            'Không yêu cầu'
                          )}
                        </StatValue>
                      </StatBody>
                    </StatCard>
                  </StatGrid>
                </QuizHeader>
                <ActionRow>
                  {userAttempts.length === 0 && !quizAttempt?.id && (
                    <PrimaryButton
                      onClick={startNewAttempt}
                      loading={startingAttempt}
                      loadingText='Đang khởi tạo...'
                      size='lg'
                    >
                      Bắt đầu làm bài
                    </PrimaryButton>
                  )}
                  {(userAttempts.at(-1)?.state === 'finished' ||
                    userAttempts.at(-1)?.state === 'abandoned') &&
                    !quizAttempt?.id && (
                      <PrimaryButton
                        onClick={startNewAttempt}
                        loading={startingAttempt}
                        loadingText='Đang khởi tạo...'
                        size='lg'
                      >
                        Thực hiện lại
                      </PrimaryButton>
                    )}
                  {userAttempts.at(-1)?.state === 'inprogress' &&
                    !quizAttempt?.id && (
                      <PrimaryButton
                        onClick={() => continueQuizAttempt(userAttempts.at(-1))}
                        size='lg'
                      >
                        Tiếp tục bài làm
                      </PrimaryButton>
                    )}
                </ActionRow>
              </>
            )}

            {userAttempts.length > 0 && (() => {
              // Compact summary: lần cuối + kết quả + ngày (DD/MM).
              // Tone follows StatusBadge variants. inprogress → warning,
              // abandoned → danger, finished+pass → success, finished+fail → danger.
              const latest = userAttempts.at(-1);
              const fmtShort = (sec) => {
                if (!sec) return '';
                const d = new Date(sec * 1000);
                return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
              };
              const latestResult = (() => {
                if (!latest) return null;
                if (latest.state === 'inprogress') return { text: 'Đang làm dở', tone: 'warning' };
                if (latest.state === 'abandoned') return { text: 'Đã huỷ', tone: 'danger' };
                if (latest.state === 'finished') {
                  if (Number(quiz?.gradepass) > 0) {
                    return Number(latest.sumgrades) >= Number(quiz.gradepass)
                      ? { text: 'Đạt', tone: 'success' }
                      : { text: 'Không đạt', tone: 'danger' };
                  }
                  return { text: 'Hoàn thành', tone: 'success' };
                }
                return null;
              })();
              const visibleAttempts = showAllAttempts ? userAttempts : userAttempts.slice(-5);
              const hiddenCount = Math.max(0, userAttempts.length - 5);

              return (
              <StyledAccordion activeKey={showHistory ? '0' : null}>
                <Accordion.Item eventKey='0'>
                  <Accordion.Header
                    onClick={() => setShowHistory((prev) => !prev)}
                  >
                    <HistorySummary>
                      <strong>Lịch sử ({userAttempts.length} lần)</strong>
                      {latestResult && (
                        <>
                          <HistorySep>-</HistorySep>
                          <span>gần nhất:</span>
                          <StatusBadge $tone={latestResult.tone}>{latestResult.text}</StatusBadge>
                          <HistorySep>·</HistorySep>
                          <span>{fmtShort(latest.timefinish || latest.timestart)}</span>
                        </>
                      )}
                    </HistorySummary>
                  </Accordion.Header>
                  <Accordion.Body>
                    {hiddenCount > 0 && !showAllAttempts && (
                      <ShowMoreRow>
                        <PrimaryButton
                          variant='ghost'
                          size='sm'
                          onClick={() => setShowAllAttempts(true)}
                        >
                          Xem thêm {hiddenCount} lần cũ hơn
                        </PrimaryButton>
                      </ShowMoreRow>
                    )}
                    <Row xs={1} md={2} className='g-3'>
                      {visibleAttempts.map((attempt) => (
                        <Col key={attempt.attempt}>
                          <HistoryCard>
                            <Card.Body>
                              <HistoryCardTitle>
                                Lần {attempt.attempt} -{' '}
                                {QUIZ_ATTEMPT_STATUS[attempt.state]}
                              </HistoryCardTitle>
                              <HistoryCardText>
                                <div>
                                  Bắt đầu:{' '}
                                  {new Date(
                                    attempt.timestart * 1000
                                  ).toLocaleString('en-GB')}
                                </div>
                                {attempt.state === 'finished' && (
                                  <>
                                    <div>
                                      Kết thúc:{' '}
                                      {new Date(
                                        attempt.timefinish * 1000
                                      ).toLocaleString('en-GB')}
                                    </div>
                                    {quiz?.timelimit > 0 && (
                                      <div>
                                        <strong>Kết quả:</strong>{' '}
                                        {attempt?.sumgrades >=
                                        quiz?.gradepass ? (
                                          <StatusBadge $tone='success'>Đạt</StatusBadge>
                                        ) : (
                                          <StatusBadge $tone='danger'>Không đạt</StatusBadge>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                                {(() => {
                                  const acc = attemptAccumulatedTime(attempt, attemptDataMap, timePerQuestion);
                                  if (!acc) return null;
                                  return (
                                    <div>
                                      <strong>Thời gian tích luỹ:</strong>{' '}
                                      {acc.status === 'abandoned'
                                        ? 'không tính (đã huỷ)'
                                        : `${formatTime(acc.seconds)} (${acc.answered} câu × ${acc.timePerQuestion} phút)`}
                                    </div>
                                  );
                                })()}
                              </HistoryCardText>
                              {attempt.state === 'inprogress' ? (
                                <PrimaryButton
                                  size='sm'
                                  onClick={() => continueQuizAttempt(attempt)}
                                >
                                  Tiếp tục bài làm
                                </PrimaryButton>
                              ) : attempt.state === 'abandoned' ? (
                                <StatusBadge $tone='danger'>Đã bị huỷ</StatusBadge>
                              ) : (
                                <PrimaryButton
                                  variant='ghost'
                                  size='sm'
                                  onClick={() =>
                                    navigate(
                                      `${PATH.ELEARNING.STUDENT.ATTEMPT_RESULT.replace(
                                        ':attemptId',
                                        attempt.id
                                      )}?c=${courseId}`
                                    )
                                  }
                                >
                                  Xem lại bài làm
                                </PrimaryButton>
                              )}
                            </Card.Body>
                          </HistoryCard>
                        </Col>
                      ))}
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </StyledAccordion>
              );
            })()}

            {quizAttempt?.id && !quizAttemptData && (
              <Spinner block label='Đang tải danh sách câu hỏi...' />
            )}

            {quizAttempt?.id && quizAttemptData?.questions?.length > 0 && (
              <QuestionsWrap>
                <PageTitle as='h2' $compact>Danh sách câu hỏi</PageTitle>
                <Row className='g-4'>
                  <Col md={9}>
                    {/* Left col: Timer + quy đổi + câu hỏi + nút tiếp tục.
                        Right col chỉ chứa Navigator + nút Nộp bài, sticky
                        khi HV cuộn xuống. */}
                    {quiz.timelimit > 0 && (
                      <div className='mb-3'>
                        <Timer
                          timestart={quizAttempt?.timestart * 1000 - 5000}
                          timelimit={quiz?.timelimit / 60}
                          onTimeUp={() => {
                            handleFinishQuiz(true);
                          }}
                        />
                      </div>
                    )}
                    {/* Khung thời gian còn lại: hiện trong lúc làm bài ngay khi
                        có số liệu thời gian hôm nay (serverTime async HOẶC Redux
                        poll). Không chờ tới lúc nộp như trước → load async,
                        không block luồng làm câu hỏi. */}
                    {(serverTime || totalTodayTime != null) && (
                      <TimeConversionCard
                        $over={isOverQuestionLimit}
                        $atLimit={isAtQuestionLimit && !isOverQuestionLimit}
                      >
                        <Card.Body style={{ padding: spacing[4] }}>
                          <ConversionCaption>Thời gian quy đổi</ConversionCaption>
                          <DailyProgressBlock>
                            <DailyProgressRow>
                              <DailyProgressLabel>Đã học hôm nay</DailyProgressLabel>
                              <DailyProgressValue
                                $tone={
                                  displayTotalTime >= maxDailySeconds
                                    ? 'danger'
                                    : displayTotalTime >= maxDailySeconds * 0.9
                                      ? 'warning'
                                      : 'success'
                                }
                              >
                                {formatTime(Math.max(0, displayTotalTime))} / {formatTime(maxDailySeconds)}
                              </DailyProgressValue>
                            </DailyProgressRow>
                            <DailyProgressTrack>
                              <DailyProgressFill
                                style={{
                                  width: `${Math.min(100, (Math.max(0, displayTotalTime) / Math.max(1, maxDailySeconds)) * 100).toFixed(1)}%`,
                                }}
                                $tone={
                                  displayTotalTime >= maxDailySeconds
                                    ? 'danger'
                                    : displayTotalTime >= maxDailySeconds * 0.9
                                      ? 'warning'
                                      : 'success'
                                }
                              />
                            </DailyProgressTrack>
                          </DailyProgressBlock>
                          {isOverQuestionLimit && (
                            <WarningInline $tone='danger'>
                              <MdWarning className='me-1' />
                              Vượt giới hạn! Cần loại bỏ {answeredCount - maxQuestions} câu khi nộp bài.
                            </WarningInline>
                          )}
                          {isAtQuestionLimit && !isOverQuestionLimit && (
                            <WarningInline $tone='warning'>
                              <MdWarning className='me-1' />
                              Đã đạt giới hạn câu hỏi. Vui lòng nộp bài.
                            </WarningInline>
                          )}
                          <ConversionRow>
                            <ConversionLabel>Được phép học:</ConversionLabel>
                            <ConversionValue $color={remainingSeconds <= 0 ? colors.danger : colors.success}>
                              {formatTime(remainingSeconds)}
                            </ConversionValue>
                          </ConversionRow>
                          <ConversionRow>
                            <ConversionLabel>Đã trả lời:</ConversionLabel>
                            <ConversionValue
                              $color={
                                maxQuestions > 0 &&
                                answeredCount >= Math.min(maxQuestions, quiz?.questioncount || maxQuestions)
                                  ? colors.danger
                                  : colors.gray900
                              }
                            >
                              {answeredCount}/{Math.min(maxQuestions, quiz?.questioncount || maxQuestions)} câu
                            </ConversionValue>
                          </ConversionRow>
                          <ConversionRow>
                            <ConversionLabel>Tích luỹ sau khi nộp:</ConversionLabel>
                            <ConversionValue>{formatTime(answeredCount * timePerQuestion * 60)}</ConversionValue>
                          </ConversionRow>
                          <ConversionFootnote>
                            Quy đổi: {timePerQuestion} phút/câu hoàn thành
                          </ConversionFootnote>
                        </Card.Body>
                      </TimeConversionCard>
                    )}
                    {quizAttemptData.questions.map((question) => {
                      const parsed = parseQuestionHTML(question.html);
                      const ItemComponent = parsed?.type === 'multianswer' ? ClozeQuestionItem : QuestionItem;
                      return (
                        <QuestionCard key={question.number}>
                          <Card.Header>
                            <Card.Title className='mb-0'>
                              Câu {question.number}
                            </Card.Title>
                          </Card.Header>
                          <Card.Body>
                            <ItemComponent
                              question={parsed}
                              onAnswerChange={handleAnswerChange}
                              slot={question.slot}
                              sequenceCheck={question.sequencecheck}
                            />
                          </Card.Body>
                        </QuestionCard>
                      );
                    })}
                    <div className='d-flex justify-content-end mt-4'>
                      {(() => {
                        // Layout logic — tách 3 trạng thái rạch ròi:
                        //   1. Câu cuối → KHÔNG render nút "tiếp tục" (nộp bài
                        //      qua nút Finish ở dưới). Nếu có thay đổi chưa
                        //      lưu thì hiện nút "Lưu câu trả lời" để đảm bảo
                        //      không mất answer trước khi nộp.
                        //   2. Có thay đổi (slotAnswers != null) → "Lưu câu
                        //      trả lời và tiếp tục".
                        //   3. Không thay đổi (đã lưu trước đó hoặc skip) →
                        //      cho phép "Tiếp tục" sang câu kế. Trước đây nút
                        //      bị disable vì `!slotAnswers` → HV không qua
                        //      được câu đã trả lời nếu reload trang.
                        const total = attemptSummary?.questions?.length || 0;
                        const isLastQuestion = total > 0 && currentPage >= total - 1;
                        const hasUnsavedAnswer = !!slotAnswers;

                        if (isLastQuestion) {
                          if (!hasUnsavedAnswer) return null;
                          return (
                            <PrimaryButton
                              loading={savingAnswers}
                              loadingText='Đang lưu câu trả lời'
                              disabled={isFinishing || isAtQuestionLimit || savingAnswers}
                              onClick={async () => {
                                try {
                                  await saveAnswer(slotAnswers, slot);
                                  setSlotAnswers(null);
                                } catch (_) { /* toast đã hiện */ }
                              }}
                            >
                              {isAtQuestionLimit
                                ? 'Đã đạt giới hạn câu hỏi hôm nay'
                                : 'Lưu câu trả lời (câu cuối)'}
                            </PrimaryButton>
                          );
                        }

                        return (
                          <PrimaryButton
                            variant={isAtQuestionLimit ? 'secondary' : 'primary'}
                            loading={savingAnswers}
                            loadingText='Đang lưu câu trả lời'
                            disabled={isFinishing || isAtQuestionLimit || savingAnswers}
                            onClick={async () => {
                              // Đợi save xong rồi mới advance — button giữ
                              // trạng thái "Đang lưu câu trả lời" cho HV
                              // thấy progress, tránh nhảy câu khi save còn
                              // in-flight. Nếu save fail → KHÔNG advance,
                              // toast đã thông báo lỗi để HV thử lại.
                              if (hasUnsavedAnswer) {
                                try {
                                  await saveAnswer(slotAnswers, slot);
                                  setSlotAnswers(null);
                                } catch (_) {
                                  return; // giữ HV ở câu hiện tại
                                }
                              }
                              setCurrentPage((prev) => prev + 1);
                            }}
                          >
                            {isAtQuestionLimit
                              ? 'Đã đạt giới hạn câu hỏi hôm nay'
                              : hasUnsavedAnswer
                              ? 'Lưu câu trả lời và tiếp tục'
                              : 'Tiếp tục'}
                          </PrimaryButton>
                        );
                      })()}
                    </div>
                  </Col>
                  <Col md={3}>
                    <RightRail>
                      <SubmitWrap>
                        <PrimaryButton
                          size='lg'
                          loading={preparingSubmit}
                          loadingText={preparingLabel}
                          disabled={savingAnswers || isFinishing}
                          onClick={async () => {
                            setPreparingSubmit(true);
                            setPreparingLabel('Đang nộp bài...');
                            // Sau 3s vẫn chưa xong → đổi nhãn để HV cảm
                            // thấy hệ thống đang xử lý (không bị đứng yên).
                            const labelTimer = setTimeout(() => setPreparingLabel('Đang kiểm tra...'), 3000);
                            try {
                              // Gate server-side: BE fetch summary + today
                              // time + compute. FE không còn tự tính (tránh
                              // bypass qua devtools / Redux edit).
                              const [gateRes] = await Promise.all([
                                elearningApi.quizSubmitGate(quizAttempt?.id, courseId),
                                getQuizAttemptSummary(quizAttempt?.id),
                              ]);
                              const gate = gateRes?.data;
                              if (!gate) {
                                toastWrapper('Không kiểm tra được điều kiện nộp bài, vui lòng thử lại.', 'error');
                                return;
                              }
                              setServerTime({
                                totalTodayTime: gate.totalTodayTime,
                                timeLimitPerDay: gate.timeLimitPerDay,
                              });
                              if (!gate.allowed) {
                                setMaxAllowedForSubmit(gate.maxQuestionsAllowed ?? 0);
                                setShowOverLimitModal(true);
                                return;
                              }
                              setShowQuestionReviewModal(true);
                            } catch (err) {
                              console.error('Error preparing submit:', err);
                              if (err?.response?.status === 429) {
                                toastWrapper(err?.response?.data?.message || 'Bạn đã gửi quá nhiều yêu cầu. Vui lòng thử lại sau.', 'warning');
                              } else if (err?.response?.status === 403) {
                                toastWrapper(err?.response?.data?.message || 'Không có quyền với bài làm này.', 'error');
                              } else {
                                toastWrapper('Có lỗi xảy ra. Vui lòng thử lại.', 'error');
                              }
                            } finally {
                              clearTimeout(labelTimer);
                              setPreparingSubmit(false);
                            }
                          }}
                        >
                          Nộp bài
                        </PrimaryButton>
                      </SubmitWrap>
                      <QuestionNavigator
                        summaryQuestions={attemptSummary?.questions || []}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                      />
                    </RightRail>
                  </Col>
                </Row>
              </QuestionsWrap>
            )}
          </>
        )}
      </Container>
      <QuestionReviewModal
        show={
          // KHÔNG đóng modal khi đang finishing — giữ nguyên context cho HV
          // thấy nút "Đang nộp bài..." loading. Modal tự đóng ở finally của
          // handleFinishQuiz sau khi BE confirm (success or fail). Cũng
          // không mở khi đang load attempt summary (race ban đầu).
          showQuestionReviewModal && !loadingAttemptSummary
        }
        onHide={() => setShowQuestionReviewModal(false)}
        summary={attemptSummary}
        onFinish={handleFinishQuiz}
        submitting={isFinishing}
      />
      <Modal show={showLimitModal} centered onHide={() => {
        limitModalDismissedRef.current = true;
        setShowLimitModal(false);
      }}>
        <Modal.Header closeButton>
          <WarnModalTitle>
            <MdWarning className='me-1' />
            Đã đạt giới hạn số câu hỏi
          </WarnModalTitle>
        </Modal.Header>
        <Modal.Body>
          <p>
            Bạn đã trả lời <strong>{answeredCount}/{maxQuestions} câu</strong> được phép hôm nay
            (quy đổi {timePerQuestion} phút/câu, thời gian còn lại: {formatTime(remainingSeconds)}).
          </p>
          {answeredCount > maxQuestions ? (
            <p className='mb-0'>
              Hệ thống sẽ tự động <strong>loại bỏ {answeredCount - maxQuestions} câu thừa</strong> (giữ lại {maxQuestions} câu đầu tiên) khi nộp bài.
            </p>
          ) : (
            <p className='mb-0'>
              Bạn không thể lưu thêm câu trả lời mới. Hãy kiểm tra lại và <strong>nộp bài</strong> khi sẵn sàng.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton
            variant='secondary'
            onClick={() => {
              limitModalDismissedRef.current = true;
              setShowLimitModal(false);
            }}
          >
            Đóng
          </PrimaryButton>
          <PrimaryButton
            variant='danger'
            loading={trimmingAnswers}
            loadingText='Đang loại bỏ câu thừa...'
            onClick={async () => {
              try {
                if (answeredCount > maxQuestions) {
                  setTrimmingAnswers(true);
                  await clearExcessAnswers(maxQuestions);
                  setTrimmingAnswers(false);
                }
                await getQuizAttemptSummary(quizAttempt?.id);
                limitModalDismissedRef.current = true;
                setShowLimitModal(false);
                setShowQuestionReviewModal(true);
              } catch (err) {
                console.error('Error preparing submit from limit modal:', err);
                toastWrapper('Có lỗi xảy ra. Vui lòng thử lại.', 'error');
                setTrimmingAnswers(false);
              }
            }}
          >
            {answeredCount > maxQuestions
              ? `Nộp bài (giữ ${maxQuestions} câu)`
              : 'Nộp bài'}
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
      <Modal show={showOverLimitModal} centered backdrop='static' keyboard={false}>
        <Modal.Header>
          <WarnModalTitle>
            <MdWarning className='me-1' />
            Vượt giới hạn thời gian quy đổi
          </WarnModalTitle>
        </Modal.Header>
        <Modal.Body>
          {maxAllowedForSubmit != null && maxAllowedForSubmit > 0 ? (
            <>
              <p>
                Bạn đã trả lời <strong>{answeredCount} câu</strong> nhưng chỉ được phép <strong>{maxAllowedForSubmit} câu</strong> dựa trên thời gian còn lại hôm nay.
              </p>
              <p>
                Nếu nộp bài, hệ thống sẽ tự động <strong>loại bỏ {answeredCount - maxAllowedForSubmit} câu thừa</strong> (giữ lại {maxAllowedForSubmit} câu đầu tiên).
              </p>
              <ModalFootnote className='mb-0'>
                Thời gian tích luỹ sau khi nộp: {formatTime(maxAllowedForSubmit * timePerQuestion * 60)}
              </ModalFootnote>
            </>
          ) : (
            <>
              <p>
                Thời gian học hôm nay của bạn đã đạt <strong>{formatTime(displayTotalTime)}</strong> (giới hạn: {formatTime(maxDailySeconds)}).
              </p>
              <p className='mb-0'>
                Bạn không còn đủ thời gian để tích luỹ thêm. Vui lòng quay lại vào ngày mai hoặc liên hệ quản trị viên.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {maxAllowedForSubmit != null && maxAllowedForSubmit > 0 && (
            <PrimaryButton
              variant='danger'
              loading={trimmingAnswers}
              loadingText='Đang xử lý...'
              onClick={async () => {
                setTrimmingAnswers(true);
                try {
                  await clearExcessAnswers(maxAllowedForSubmit);
                  await getQuizAttemptSummary(quizAttempt?.id);
                  setShowOverLimitModal(false);
                  setMaxAllowedForSubmit(null);
                  setShowQuestionReviewModal(true);
                } catch (err) {
                  console.error('Error trimming answers:', err);
                  toastWrapper('Có lỗi xảy ra khi loại bỏ câu thừa. Vui lòng thử lại.', 'error');
                } finally {
                  setTrimmingAnswers(false);
                }
              }}
            >
              {`Nộp bài (giữ ${maxAllowedForSubmit} câu)`}
            </PrimaryButton>
          )}
          <PrimaryButton variant='secondary' disabled={trimmingAnswers} onClick={() => {
            setShowOverLimitModal(false);
            setMaxAllowedForSubmit(null);
          }}>
            Đóng
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    </PageWrap>
  );
}

/* ─── styled ─── */

const PageWrap = styled.div`
  min-height: 100vh;
  background: ${colors.background};
  overflow-y: auto;
  position: relative;
`;

const PageTitle = styled.h2`
  font-size: ${typography.size.h2};
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};
  margin-bottom: ${(p) => (p.$compact ? spacing[4] : spacing[3])};

  ${MOBILE} {
    font-size: ${typography.size.h3};
  }
`;

const QuizHeader = styled.div`
  margin-bottom: ${spacing[5]};
`;

const QuizTitleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  margin-bottom: ${spacing[4]};
`;

const QuizBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 2px ${spacing[2]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.primary};
  background: ${colors.primarySoft || 'rgba(99,102,241,0.12)'};
  border-radius: ${radius.full || '999px'};
`;

// Flex chứ không phải grid cột cố định: số ô thay đổi theo quiz — ô "Thời
// gian làm bài" chỉ render khi quiz.timelimit khác 0. Grid 4 cột cũ khiến
// quiz không giới hạn thời gian (3 ô) chỉ dùng 3/4 hàng, ô "Môn học" bị bó
// vào 1/4 dù tên môn dài nhất. Tỉ lệ đặt trong StatCard (flex-grow), không
// đặt ở đây, để CSS không cần biết có bao nhiêu ô.
const StatGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
`;

// Tone palette — map sang DS color tokens. Fallback inline cho token
// chưa có (vd warningSoft) để không crash khi DS shim chưa export.
const STAT_TONE = {
  subject: { soft: colors.gray100 || '#f3f4f6', solid: colors.gray700 },
  time:    { soft: colors.primarySoft || 'rgba(99,102,241,0.12)', solid: colors.primary },
  count:   { soft: colors.warningSoft || 'rgba(245,158,11,0.12)', solid: colors.warning || '#f59e0b' },
  pass:    { soft: colors.successSoft || 'rgba(34,197,94,0.12)', solid: colors.success },
};

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[3]};
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  min-width: 0;
  transition: box-shadow 200ms ease, transform 200ms ease;

  /* "Môn học" ($wide) rộng gấp đôi mỗi ô còn lại. Tính từ flex-basis 0 nên
     tỉ lệ do flex-grow quyết định, không phụ thuộc tên môn dài ngắn:
       3 ô (quiz không giới hạn thời gian) -> 1/2 + 1/4 + 1/4
       4 ô (có ô "Thời gian làm bài")      -> 2/5 + 1/5 x 3, vẫn một hàng
     Đặt ngay trong StatCard, KHÔNG dùng selector con StatGrid > *: hai selector đó cùng
     specificity nên thứ tự inject của styled-components sẽ quyết định bên
     nào thắng — một cuộc đua không cần thiết. */
  flex: ${(p) => (p.$wide ? 2 : 1)} 1 0;

  /* Hai breakpoint hẹp phải đặt lại flex-basis. Nếu để basis 0, flex-grow
     2 vs 1 vẫn chia hàng lệch thay vì 2 ô/hàng rồi 1 ô/hàng. Trừ nửa gap
     để hai ô + gap vừa đúng 100%, không bị đẩy xuống dòng. */
  @media (max-width: 992px) {
    flex-basis: calc(50% - ${spacing[3]} / 2);
  }
  ${MOBILE} {
    flex-basis: 100%;
  }

  &:hover {
    box-shadow: ${shadow.md};
  }
`;

const StatIcon = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: ${radius.md};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => STAT_TONE[p.$tone]?.soft || colors.gray100 || '#f3f4f6'};
  color: ${(p) => STAT_TONE[p.$tone]?.solid || colors.gray700};
`;

const StatBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const StatLabel = styled.span`
  font-size: ${typography.size.xs};
  color: ${colors.gray500};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: ${typography.weight.semibold};
  margin-bottom: 2px;
`;

const StatValue = styled.span`
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  /* Tránh overflow khi tên môn dài — ellipsis 1 dòng. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatUnit = styled.span`
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  color: ${colors.gray500};
  margin-left: 2px;
`;

const StatPlaceholder = styled.span`
  display: inline-block;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  color: ${colors.gray400};
  font-style: italic;
`;

const ActionRow = styled.div`
  margin-bottom: ${spacing[5]};
  display: flex;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

const QuestionsWrap = styled.div`
  margin-top: ${spacing[8]};

  ${MOBILE} {
    margin-top: ${spacing[6]};
  }
`;

const QuestionCard = styled(Card)`
  margin-bottom: ${spacing[3]};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};

  .card-header {
    background: ${colors.surfaceMuted};
    border-bottom: 1px solid ${colors.border};
  }
`;

const TimeConversionCard = styled(Card)`
  margin-bottom: ${spacing[4]};
  border: 1px solid
    ${({ $over, $atLimit }) =>
      $over || $atLimit ? colors.danger : colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  background: ${({ $over, $atLimit }) =>
    $over ? colors.dangerSoft : $atLimit ? colors.warningSoft : colors.surface};
  font-size: ${typography.size.sm};
`;

const ConversionCaption = styled.div`
  color: ${colors.gray500};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  margin-bottom: ${spacing[2]};
  letter-spacing: 0.5px;
`;

const ConversionRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing[1]};
`;

const ConversionLabel = styled.span`
  color: ${colors.gray500};
`;

const ConversionValue = styled.span`
  font-weight: ${typography.weight.semibold};
  color: ${(p) => p.$color || colors.gray900};
`;

const ConversionFootnote = styled.div`
  font-size: ${typography.size.xs};
  color: ${colors.gray400};
  font-style: italic;
`;

const WarningInline = styled.div`
  color: ${({ $tone }) => ($tone === 'danger' ? colors.danger : colors.warning)};
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.sm};
  margin-bottom: ${spacing[2]};
`;

const DailyProgressBlock = styled.div`
  margin-bottom: ${spacing[3]};
`;

const DailyProgressRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${spacing[1]};
`;

const DailyProgressLabel = styled.span`
  color: ${colors.gray500};
  font-size: ${typography.size.xs};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DailyProgressValue = styled.span`
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.sm};
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) =>
    $tone === 'danger'
      ? colors.danger
      : $tone === 'warning'
        ? colors.warning
        : colors.success};
`;

const DailyProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: ${colors.gray100 || '#f3f4f6'};
  overflow: hidden;
`;

const DailyProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease, background 0.2s ease;
  background: ${({ $tone }) =>
    $tone === 'danger'
      ? colors.danger
      : $tone === 'warning'
        ? colors.warning
        : colors.success};
`;

const SubmitWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-bottom: ${spacing[3]};

  /* Stretch nút "Nộp bài" full-width của cột phải để dễ bấm + cân đối
     visual với QuestionNavigator card phía dưới. */
  button {
    width: 100%;
  }
`;

// Right rail bọc Submit + Navigator thành 1 khối sticky duy nhất → cuộn
// câu hỏi ở cột trái thì cột phải vẫn cố định ở viewport. Mobile (xs) tự
// xuống dòng thường nhờ Bootstrap col stack.
const RightRail = styled.div`
  position: sticky;
  top: ${spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};

  ${MOBILE} {
    position: static;
  }
`;

const StyledAccordion = styled(Accordion)`
  margin-bottom: ${spacing[5]};

  .accordion-item {
    border: 1px solid ${colors.border};
    border-radius: ${radius.lg};
    overflow: hidden;
    background: ${colors.surface};
  }

  .accordion-button {
    font-size: ${typography.size.md};
    font-weight: ${typography.weight.semibold};
    color: ${colors.gray900};
    background: ${colors.surface};
  }

  .accordion-button:not(.collapsed) {
    background: ${colors.primarySoft};
    color: ${colors.primary};
    box-shadow: none;
  }

  .accordion-button:focus {
    box-shadow: ${shadow.focus};
    border-color: transparent;
  }
`;

const HistoryCard = styled(Card)`
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  background: ${colors.surface};
`;

const HistoryCardTitle = styled(Card.Title)`
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.semibold};
  color: ${colors.gray900};
  margin-bottom: ${spacing[2]};
`;

const HistoryCardText = styled.div`
  font-size: ${typography.size.sm};
  color: ${colors.gray700};
  margin-bottom: ${spacing[3]};

  > div {
    margin-bottom: ${spacing[1]};
  }
`;

const StatusBadge = styled(Badge)`
  background: ${({ $tone }) =>
    $tone === 'success'
      ? colors.successSoft
      : $tone === 'warning'
      ? (colors.warningSoft || 'rgba(245,158,11,0.12)')
      : colors.dangerSoft} !important;
  color: ${({ $tone }) =>
    $tone === 'success'
      ? colors.success
      : $tone === 'warning'
      ? (colors.warning || '#f59e0b')
      : colors.danger} !important;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  padding: ${spacing[1]} ${spacing[2]};
`;

// Summary inline cho Accordion.Header: "Lịch sử (N lần) — gần nhất: Đạt · DD/MM"
// Wrap bằng flex để badge + ngày tháng align cùng baseline. font cỡ vừa
// — không quá nhỏ vì là entry điểm cho user.
const HistorySummary = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[1]} ${spacing[2]};
  font-size: ${typography.size.sm};
  color: ${colors.gray700};

  strong {
    color: ${colors.gray900};
    font-weight: ${typography.weight.semibold};
  }
`;

const HistorySep = styled.span`
  color: ${colors.gray400};
`;

// Row chứa nút "Xem thêm N lần cũ hơn" — căn phải để gần Accordion border,
// margin-bottom đủ để tách với grid attempts kế tiếp.
const ShowMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing[3]};
`;

const WarnModalTitle = styled(Modal.Title)`
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${colors.danger};
  display: flex;
  align-items: center;
`;

const ModalFootnote = styled.p`
  font-size: ${typography.size.sm};
  color: ${colors.gray500};
`;

export default ElearningStudentTestDetailPage;
